import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Table } from '@shared/ui/Table';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryEmpty,
  RegistryFilterSelect,
  RegistrySearchBox,
} from '@shared/ui/RegistryShell';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { useListKadryAuditInfiniteQuery, type KadryAuditFilter } from '@shared/api';
import {
  KADRY_ACTOR_KIND_LABELS,
  KADRY_AUDIT_ACTION_GROUPS,
  KADRY_AUDIT_ACTION_LABELS,
  KADRY_AUDIT_ENTITY_LABELS,
  KADRY_AUDIT_RESULT_LABELS,
  kadryDateTime,
  type KadryAuditRecord,
} from '@entities/kadry';
import {
  KadryBannerGap,
  KadryErrorBanner,
  KadryMono,
  KadryMuted,
  KadryPageHeader,
  KadryPerson,
  KadryTableScroll,
} from './ui';

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, max-content)) minmax(200px, 320px) auto;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Nowrap = styled.td`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
  overflow-wrap: anywhere;
`;

const More = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)} 0;
`;

/** Значения `meta` кратко: `from=draft, to=active`. Журнал хранится без ПДн. */
function metaText(meta: Record<string, unknown>): string {
  const parts = Object.entries(meta ?? {}).map(([k, v]) => {
    const val = typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v);
    return `${k}=${val}`;
  });
  return parts.join(', ');
}

/** Журнал аудита модуля (`hr_admin`, `auditor`): события без ПДн, новые сверху. */
export function KadryAudit() {
  const [actionPrefix, setActionPrefix] = useState('');
  const [entity, setEntity] = useState('');
  const [entityIdInput, setEntityIdInput] = useState('');
  const entityId = useDebouncedValue(entityIdInput.trim(), 400);

  const filter: KadryAuditFilter = useMemo(
    () => ({
      action: actionPrefix || undefined,
      entity: entity || undefined,
      entity_id: entityId || undefined,
    }),
    [actionPrefix, entity, entityId],
  );

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useListKadryAuditInfiniteQuery(filter);
  const rows: KadryAuditRecord[] = useMemo(() => data?.pages.flat() ?? [], [data]);
  const filtered = Boolean(actionPrefix || entity || entityId);

  /** Исполнитель: сотрудник — ФИО с сервера (или полный UUID), иначе вид (система, кандидат). */
  function actor(r: KadryAuditRecord) {
    if (r.actor_kind === 'user') return <KadryPerson id={r.actor_id} name={r.actor_name} />;
    return (
      <>
        {KADRY_ACTOR_KIND_LABELS[r.actor_kind] ?? r.actor_kind}
        {r.actor_id && (
          <Sub>
            <KadryMono>{r.actor_id}</KadryMono>
          </Sub>
        )}
      </>
    );
  }

  return (
    <>
      <KadryPageHeader
        title="Журнал"
        subtitle="Все изменения в модуле: кто, когда, что и с каким результатом. Журнал только дополняется и не содержит персональных данных кандидатов."
        actions={
          <Button type="button" $size="sm" $variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching && !isFetchingNextPage ? 'Обновляем…' : 'Обновить'}
          </Button>
        }
      />

      <Toolbar>
        <RegistryFilterSelect
          value={actionPrefix}
          onChange={(e) => setActionPrefix(e.target.value)}
          aria-label="Фильтр по действию"
        >
          <option value="">Все действия</option>
          {KADRY_AUDIT_ACTION_GROUPS.map((g) => (
            <optgroup key={g.prefix} label={g.label}>
              <option value={g.prefix}>{g.label}: все</option>
              {Object.entries(KADRY_AUDIT_ACTION_LABELS)
                .filter(([code]) => code.startsWith(g.prefix))
                .map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
            </optgroup>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={entity}
          onChange={(e) => setEntity(e.target.value)}
          aria-label="Фильтр по объекту"
        >
          <option value="">Все объекты</option>
          {Object.entries(KADRY_AUDIT_ENTITY_LABELS).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistrySearchBox
          value={entityIdInput}
          onChange={(e) => setEntityIdInput(e.target.value)}
          placeholder="ID объекта (точное совпадение)"
          aria-label="ID объекта"
        />
        {filtered && (
          <Button
            type="button"
            $size="sm"
            $variant="ghost"
            onClick={() => {
              setActionPrefix('');
              setEntity('');
              setEntityIdInput('');
            }}
          >
            Сбросить
          </Button>
        )}
      </Toolbar>

      {error ? (
        <KadryBannerGap>
          <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить журнал" />
        </KadryBannerGap>
      ) : null}

      <Card>
        <KadryTableScroll>
          <Table>
            <thead>
              <tr>
                <th>Время</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>Результат</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td>
                      <Skeleton $width={120} />
                    </td>
                    <td>
                      <Skeleton $width={140} />
                    </td>
                    <td>
                      <Skeleton $width="70%" />
                    </td>
                    <td>
                      <Skeleton $width={120} />
                    </td>
                    <td>
                      <Skeleton $width={80} $height={20} $radius="999px" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <RegistryEmpty colSpan={5}>
                    {filtered ? 'По выбранным условиям событий нет.' : 'Событий пока нет.'}
                  </RegistryEmpty>
                </tr>
              ) : (
                rows.map((r) => {
                  const res = KADRY_AUDIT_RESULT_LABELS[r.result];
                  const meta = metaText(r.meta);
                  return (
                    <tr key={r.id}>
                      <Nowrap>{kadryDateTime(r.at)}</Nowrap>
                      <td>{actor(r)}</td>
                      <td>
                        {KADRY_AUDIT_ACTION_LABELS[r.action] ?? r.action}
                        <Sub>
                          <KadryMono>{r.action}</KadryMono>
                          {meta ? ` · ${meta}` : ''}
                        </Sub>
                      </td>
                      <td>
                        {KADRY_AUDIT_ENTITY_LABELS[r.entity] ?? r.entity}
                        {r.entity_id && (
                          <Sub>
                            <KadryMono>{r.entity_id}</KadryMono>
                          </Sub>
                        )}
                      </td>
                      <td>
                        <Badge $tone={res?.tone ?? 'neutral'}>{res?.label ?? r.result}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </KadryTableScroll>
        {rows.length > 0 && (
          <More>
            {hasNextPage ? (
              <Button
                type="button"
                $size="sm"
                $variant="secondary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            ) : (
              <KadryMuted>Показаны все события ({rows.length}).</KadryMuted>
            )}
          </More>
        )}
      </Card>
    </>
  );
}
